/**
 * AudioRestorationEngine — orchestrator for the audio-restoration tool's
 * module chain.
 *
 * Each module is registered with `register(name, loaderFn)`, where loaderFn
 * is an async function that imports / returns the module's process()
 * implementation:
 *
 *   process(inputBuffer, params, ctx) => Promise<{ output: AudioBuffer, impactDb: number, meta?: object }>
 *
 * The engine runs them sequentially in the user-chosen order, threading the
 * output of each into the input of the next, freeing intermediate buffers
 * after each step. AbortSignal is honoured at every module boundary so a
 * user click on "Cancel" stops the chain within one frame of work.
 *
 * Why this exists separately from the modules
 * ───────────────────────────────────────────
 * Keeping the loader/lifecycle logic out of each module lets us:
 *  – tree-shake unused modules (a user who only toggles declick never
 *    fetches restoration-dehiss.js)
 *  – instrument impact-dB uniformly: every module reports a single number,
 *    the engine renders the chip in the UI
 *  – handle errors uniformly: a failing module returns its input unchanged
 *    instead of aborting the whole chain (graceful degradation)
 */
(function () {
    'use strict';

    const REGISTRY = new Map();

    function register(name, loader) {
        REGISTRY.set(name, { loader, instance: null });
    }

    async function _resolve(name) {
        const entry = REGISTRY.get(name);
        if (!entry) throw new Error('Unknown restoration module: ' + name);
        if (entry.instance) return entry.instance;
        entry.instance = await entry.loader();
        return entry.instance;
    }

    /**
     * Compute RMS dB delta between two AudioBuffers of equal shape. Used as a
     * uniform "impact" metric — works for any module regardless of what it
     * actually does to the signal.
     */
    function rmsDeltaDb(before, after) {
        const ch = Math.min(before.numberOfChannels, after.numberOfChannels);
        let sumB = 0, sumA = 0, n = 0;
        const step = Math.max(1, Math.floor(before.length / 16384));
        for (let c = 0; c < ch; c++) {
            const b = before.getChannelData(c);
            const a = after.getChannelData(c);
            const len = Math.min(b.length, a.length);
            for (let i = 0; i < len; i += step) {
                sumB += b[i] * b[i];
                sumA += a[i] * a[i];
                n++;
            }
        }
        if (!n) return 0;
        const rmsB = Math.sqrt(sumB / n);
        const rmsA = Math.sqrt(sumA / n);
        if (rmsB < 1e-7 || rmsA < 1e-7) return 0;
        return 20 * Math.log10(rmsA / rmsB);
    }

    /**
     * Clone an AudioBuffer. Used as the safe "input unchanged" fallback when
     * a module is disabled or fails. Output is a fresh buffer so the caller
     * can freely mutate without leaking back into upstream state.
     */
    function cloneBuffer(src, ctx) {
        const out = ctx.createBuffer(src.numberOfChannels, src.length, src.sampleRate);
        for (let c = 0; c < src.numberOfChannels; c++) {
            out.copyToChannel(src.getChannelData(c), c);
        }
        return out;
    }

    function getOfflineContext(buffer) {
        // OfflineAudioContext is used here only as an AudioBuffer factory —
        // the engine never starts rendering. createBuffer() is the actual API.
        if (typeof OfflineAudioContext === 'undefined') {
            throw new Error('OfflineAudioContext not available');
        }
        return new OfflineAudioContext(buffer.numberOfChannels, 1, buffer.sampleRate);
    }

    /**
     * Run the configured module chain on an input AudioBuffer.
     *
     * @param {AudioBuffer} inputBuffer
     * @param {Array<{name:string, params?:object, enabled?:boolean}>} chain
     * @param {object} [opts]
     * @param {AbortSignal} [opts.signal]
     * @param {function(stage:string, ratio:number):void} [opts.onProgress]
     * @param {boolean} [opts.captureIntermediates] — when true, the returned
     *     object carries an `intermediates` map { moduleName → AudioBuffer }
     *     with the chain state AFTER each module. Used by stem-export to
     *     let the user download "rumble-removed only", "hum-removed only",
     *     etc. without rerunning the chain. Allocates O(chainLength × N) memory
     *     so disabled by default.
     * @returns {Promise<{output:AudioBuffer, impact:Array<{name:string, db:number, meta:object}>, intermediates?:Object<string,AudioBuffer>}>}
     */
    async function runChain(inputBuffer, chain, opts) {
        opts = opts || {};
        const signal = opts.signal;
        const onProgress = opts.onProgress || function () {};
        const captureIntermediates = !!opts.captureIntermediates;
        const ctx = getOfflineContext(inputBuffer);

        let current = inputBuffer;
        const impact = [];
        const intermediates = captureIntermediates ? {} : null;
        const enabled = chain.filter(c => c.enabled !== false);
        const total = Math.max(1, enabled.length);

        for (let i = 0; i < enabled.length; i++) {
            if (signal && signal.aborted) {
                const err = new Error('aborted');
                err.name = 'AbortError';
                throw err;
            }
            const step = enabled[i];
            onProgress(step.name, i / total);

            try {
                const mod = await _resolve(step.name);
                const beforeBuf = current;
                const result = await mod.process(beforeBuf, step.params || {}, { ctx, signal });
                if (!result || !result.output) {
                    // Module returned nothing — keep input, log zero impact.
                    impact.push({ name: step.name, db: 0, meta: { skipped: true } });
                    continue;
                }
                const db = (typeof result.impactDb === 'number')
                    ? result.impactDb
                    : rmsDeltaDb(beforeBuf, result.output);
                impact.push({ name: step.name, db: db, meta: result.meta || {} });
                current = result.output;
                if (intermediates) intermediates[step.name] = current;
            } catch (err) {
                if (err && err.name === 'AbortError') throw err;
                // Graceful degradation — log and continue with input unchanged.
                if (typeof console !== 'undefined') {
                    console.warn('[restoration] module', step.name, 'failed:', err && err.message);
                }
                impact.push({ name: step.name, db: 0, meta: { error: String(err && err.message || err) } });
            }
        }
        onProgress('done', 1);
        const result = { output: current, impact: impact };
        if (intermediates) result.intermediates = intermediates;
        return result;
    }

    /* Versioned URLs for the lazily-loaded DSP modules, supplied by the page.
       WHY this indirection exists: the loader below used to hard-code
       `/js/restoration-{name}.js`. Static assets are served max-age=31536000
       (with `immutable` on the intl front), so a returning visitor kept the
       DSP module they first downloaded for up to a year while the deploy
       reported success — the documented cache-poisoning trap, reached here
       through a hand-built URL rather than a <script> tag.
       The page calls configure() with @light()-generated, version-stamped URLs; the
       tree-shaking that made lazy loading worth having is untouched, because
       we still fetch only the modules the user actually switched on. */
    const MODULE_URLS = Object.create(null);

    function configure(opts) {
        if (!opts || !opts.urls) return;
        Object.keys(opts.urls).forEach(function (k) { MODULE_URLS[k] = opts.urls[k]; });
    }

    window.AudioRestorationEngine = {
        register: register,
        runChain: runChain,
        configure: configure,
        // Internal helpers exposed for tests / module sharing
        _rmsDeltaDb: rmsDeltaDb,
        _cloneBuffer: cloneBuffer,
        _moduleUrls: MODULE_URLS,
    };

    // Auto-register the bundled modules. Each loader returns the module's
    // public { process } namespace.
    function loaderFor(name) {
        return async function () {
            // Already on the page (preloaded, or a second chain run) — never
            // inject a duplicate. The old id-guard only recognised scripts the
            // loader itself had appended, so a preloaded module was fetched a
            // second time from the unversioned path.
            if (window['Restoration_' + name]) return window['Restoration_' + name];

            await new Promise(function (resolve, reject) {
                const id = 'restoration-' + name + '-script';
                const existing = document.getElementById(id);
                if (existing) {
                    existing.addEventListener('load', function () { resolve(); });
                    existing.addEventListener('error', function () { reject(new Error('failed to load ' + id)); });
                    if (window['Restoration_' + name]) resolve();
                    return;
                }
                const s = document.createElement('script');
                s.id = id;
                s.src = MODULE_URLS[name] || ('/js/restoration-' + name + '.js');
                s.async = true;
                s.onload = function () { resolve(); };
                s.onerror = function () { reject(new Error('failed to load ' + s.src)); };
                document.head.appendChild(s);
            });
            const ns = window['Restoration_' + name];
            if (!ns || typeof ns.process !== 'function') {
                throw new Error('restoration-' + name + ' did not export Restoration_' + name + '.process');
            }
            return ns;
        };
    }

    [
        'declip',
        'monoize',
        'derumble',
        'dehum',
        'declick',
        'decrackle',
        'dehiss',
        'deemphasis',
        'eq',
        'loudness',
    ].forEach(function (name) { register(name, loaderFor(name)); });
})();
